import type {
  CaptionActiveTextStyleDto,
  CaptionShadowDto,
  CaptionTextStyleDto,
  CaptionBackgroundStyleDto,
  CaptionPositionDto,
} from "@repo/api/caption";
import { CSSProperties } from "react";
import { hexToRgba } from "../../utils/color.ts";

export const CAPTION_PREVIEW_TEXT =
  "The quick brown fox, jumps over the lazy dog";

export const CAPTION_PREVIEW_CHARACTER_START_TIMES = [
  0, 0.093, 0.128, 0.151, 0.209, 0.244, 0.29, 0.337, 0.372, 0.406, 0.464, 0.511,
  0.569, 0.604, 0.65, 0.708, 0.789, 0.859, 1.033, 1.161, 1.231, 1.3, 1.37,
  1.428, 1.474, 1.521, 1.567, 1.614, 1.672, 1.707, 1.741, 1.765, 1.811, 1.834,
  1.858, 1.881, 1.927, 1.974, 2.067, 2.159, 2.229, 2.287, 2.334, 2.485,
];

export function parseShadowToCSS(
  shadow?: CaptionShadowDto,
): string | undefined {
  if (!shadow) return undefined;

  const x = shadow.x ?? 0;
  const y = shadow.y ?? 0;
  const blur = shadow.blur ?? 0;
  const color = shadow.color ?? "#000000";
  const alpha = (shadow.opacity ?? 100) / 100;

  return `${x}px ${y}px ${blur}px ${hexToRgba(color, alpha)}`;
}

export function parseTextStyleToCSS(
  style: CaptionTextStyleDto,
  scale?: number,
): CSSProperties {
  const fontSize = style.fontSize ? style.fontSize * (scale ?? 1) : undefined;
  const color = style.color ?? "#FFFFFF";
  const opacity = style.opacity ?? 100;

  return {
    fontSize,
    fontWeight: style.fontWeight,
    fontStyle: style.fontStyle,
    opacity: opacity / 100,
    color: color,
    textTransform: style.textTransform,
    textShadow: parseShadowToCSS(style.textShadow),
    letterSpacing: style.letterSpacing,
  };
}

export function parseActiveTextStyleToCSS(
  style?: CaptionActiveTextStyleDto,
): CSSProperties {
  const color = style?.color ?? "#FFFFFF";
  const opacity = style?.opacity ?? 100;

  return {
    color: color,
    opacity: opacity / 100,
  };
}

function parsePaddingToCSS(
  padding?: {
    top: number;
    right: number;
    bottom: number;
    left: number;
  },
  scale?: number,
): string | undefined {
  if (!padding) return undefined;

  const s = scale ?? 1;
  const { top, right, bottom, left } = padding;

  return `${top * s}px ${right * s}px ${bottom * s}px ${left * s}px`;
}

export function parseCaptionBackgroundStyleToCSS(
  style?: CaptionBackgroundStyleDto,
  scale?: number,
): CSSProperties {
  if (!style) return {};

  const s = scale ?? 1;
  const color = style.color ?? "#FFFFFF";
  const opacity = style.opacity ?? 0;
  const alpha = opacity / 100;

  return {
    background: hexToRgba(color, alpha),
    borderRadius: (style.borderRadius ?? 8) * s,
    padding: parsePaddingToCSS(
      style.padding ?? {
        top: 8,
        right: 24,
        bottom: 8,
        left: 24,
      },
      scale,
    ),
    boxShadow: parseShadowToCSS(style.boxShadow),
  };
}

export const DEFAULT_ASPECT_RATIOS_POSITION: CaptionPositionDto = {
  "16:9": {
    y: 15,
  },
  "9:16": {
    y: 20,
  },
  "1:1": {
    y: 20,
  },
};
